import { motion } from 'framer-motion'
import { 
  Cookie,
  Calendar,
  Settings,
  Shield,
  BarChart,
  Target,
  Mail
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import MarketingNav from '@/components/marketing/MarketingNav'
import MarketingFooter from '@/components/marketing/MarketingFooter'

const lastUpdated = 'January 15, 2026'

const cookieTypes = [
  {
    icon: Shield,
    title: 'Essential Cookies',
    required: true,
    description: 'These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you such as setting your privacy preferences, logging in, or filling in forms.',
    cookies: [
      { name: 'session_id', purpose: 'User authentication and session management', duration: 'Session' },
      { name: 'csrf_token', purpose: 'Cross-site request forgery protection', duration: 'Session' },
      { name: 'cookie_consent', purpose: 'Stores your cookie preferences', duration: '1 year' },
      { name: 'device_id', purpose: 'Device identification for security', duration: '1 year' },
    ],
  },
  {
    icon: Settings,
    title: 'Functional Cookies',
    required: false,
    description: 'These cookies enable the website to provide enhanced functionality and personalisation. They may be set by us or by third-party providers whose services we have added to our pages.',
    cookies: [
      { name: 'preferences', purpose: 'Stores UI preferences (theme, language)', duration: '1 year' },
      { name: 'recent_projects', purpose: 'Remembers recently viewed projects', duration: '30 days' },
      { name: 'editor_settings', purpose: 'CAD editor preferences', duration: '1 year' },
      { name: 'printer_prefs', purpose: 'Default printer settings', duration: '1 year' },
    ],
  },
  {
    icon: BarChart,
    title: 'Analytics Cookies',
    required: false,
    description: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most and least popular and see how visitors move around the site.',
    cookies: [
      { name: '_analytics', purpose: 'Privacy-focused analytics (Plausible)', duration: '1 year' },
      { name: 'session_events', purpose: 'Aggregated session analytics', duration: 'Session' },
      { name: 'feature_usage', purpose: 'Feature usage statistics', duration: '30 days' },
    ],
  },
  {
    icon: Target,
    title: 'Marketing Cookies',
    required: false,
    description: 'These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.',
    cookies: [
      { name: 'referral_source', purpose: 'Tracks referral attribution', duration: '30 days' },
      { name: 'campaign_id', purpose: 'Marketing campaign tracking', duration: '30 days' },
    ],
  },
]

const sections = [
  {
    title: 'What Are Cookies?',
    content: `Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently and provide information to the owners of the site.

We use cookies and similar technologies (such as local storage and pixels) to:
- Keep you signed in
- Remember your preferences
- Understand how you use our service
- Improve our products and services
- Protect against fraud and abuse`
  },
  {
    title: 'Your Cookie Choices',
    content: `When you first visit 3DMakerly, you'll see a cookie banner that allows you to:

**Accept All:** Enable all cookies, including analytics and marketing.

**Essential Only:** Only enable cookies required for the site to function.

**Customise:** Choose which categories of cookies to enable.

You can change your preferences at any time through the cookie settings in the footer of our website or in your account settings.

**Browser Settings:**
You can also control cookies through your browser settings. Most browsers allow you to:
- See what cookies you have and delete them
- Block third-party cookies
- Block cookies from particular sites
- Block all cookies
- Delete all cookies when you close your browser

Note: Blocking all cookies may impact your experience and some features may not work correctly.`
  },
  {
    title: 'Third-Party Cookies',
    content: `We use a limited number of third-party services that may set their own cookies:

**Stripe:** Payment processing. Subject to Stripe's privacy policy.

**Cloudflare:** Security and performance. Subject to Cloudflare's privacy policy.

**Plausible:** Privacy-focused analytics that doesn't use cookies by default.

We do not allow third-party advertising cookies on our platform.`
  },
  {
    title: 'Do Not Track',
    content: `Some browsers have a "Do Not Track" feature that signals to websites that you do not want to have your online activity tracked. We respect the Global Privacy Control (GPC) signal and will treat it as a valid opt-out of analytics and marketing cookies.`
  },
  {
    title: 'Updates to This Policy',
    content: `We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of material changes by posting the new policy on this page and updating the "Last Updated" date.`
  },
]

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-surface-950">
      <MarketingNav />
      
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-20" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-warning-500/10 border border-warning-500/20 mb-8"
          >
            <Cookie className="w-4 h-4 text-warning-400" />
            <span className="text-sm font-medium text-warning-300">Legal</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Cookie Policy
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-4 text-surface-400 mb-8"
          >
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Last updated: {lastUpdated}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Button variant="secondary">
              <Settings className="w-4 h-4" />
              Manage Cookie Preferences
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Cookie Types */}
      <section className="py-16 border-y border-surface-800 bg-surface-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8">Types of Cookies We Use</h2>
          
          <div className="space-y-8">
            {cookieTypes.map((type, index) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-xl bg-surface-800/50 border border-surface-700/50"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary-500/10">
                      <type.icon className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white">{type.title}</h3>
                      {type.required && (
                        <span className="text-xs text-surface-500">Always active</span>
                      )}
                    </div>
                  </div>
                  {!type.required && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-surface-500">Optional</span>
                      <div className="w-12 h-6 rounded-full bg-surface-700 relative cursor-pointer">
                        <div className="absolute left-1 top-1 w-4 h-4 rounded-full bg-surface-500" />
                      </div>
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-surface-400 mb-4">{type.description}</p>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-surface-700">
                        <th className="text-left py-2 text-surface-500 font-medium">Cookie Name</th>
                        <th className="text-left py-2 text-surface-500 font-medium">Purpose</th>
                        <th className="text-left py-2 text-surface-500 font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {type.cookies.map((cookie) => (
                        <tr key={cookie.name} className="border-b border-surface-800">
                          <td className="py-2 text-white font-mono text-xs">{cookie.name}</td>
                          <td className="py-2 text-surface-400">{cookie.purpose}</td>
                          <td className="py-2 text-surface-500">{cookie.duration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {sections.map((section) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-white mb-4">{section.title}</h2>
              <div className="text-surface-300 leading-relaxed whitespace-pre-line">
                {section.content.split('\n').map((paragraph, i) => {
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <h3 key={i} className="text-lg font-semibold text-white mt-4 mb-2">
                        {paragraph.replace(/\*\*/g, '')}
                      </h3>
                    )
                  }
                  if (paragraph.startsWith('- ')) {
                    return (
                      <li key={i} className="ml-4 text-surface-400">
                        {paragraph.substring(2)}
                      </li>
                    )
                  }
                  if (paragraph.trim() === '') return <br key={i} />
                  return <p key={i} className="mb-4">{paragraph}</p>
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 bg-surface-900/30 border-t border-surface-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-500/10 mb-4">
            <Mail className="w-6 h-6 text-primary-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Questions about cookies?</h2>
          <p className="text-surface-400 mb-4">
            Contact us at{' '}
            <a href="mailto:privacy@3dmakerly.com" className="text-primary-400 hover:text-primary-300 transition-colors">
              privacy@3dmakerly.com
            </a>
          </p>
          <Button variant="secondary">
            <Settings className="w-4 h-4" />
            Manage Cookie Preferences
          </Button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
